import React, { useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useHistory, useLocation } from 'react-router-dom'
import {
  useBaseTransforms,
  useCreateSlotProfile,
  useDeleteSlotProfile,
  useImpactModelProfiles,
  useImpactModels,
  useUpdateSlotProfileTransform,
} from '../hooks/climateAnalytics'
import { usePropertyTypes } from '../hooks/locations'
import { ClimateAnalyticsPermissions, usePermissions } from 'balkerne-core/roles'
import siteMap from '../siteMap'
import type { SlotTransformSpec, Transform } from '../types/adaptations'
import ImpactFunctionBrowserDialog from '../components/climateAnalytics/configure/ImpactFunctionBrowserDialog'
import {
  ImpactFunctionVisualisation,
  TransformEditorDialog,
  TransformPreviewDialog,
} from '../components/climateAnalytics/configure/TransformEditor'
import { impactFunctionOptions } from '../components/climateAnalytics/configure/ImpactFunctionOptions'

type UUID = string

interface Slot {
  id: number
  ref: string
  label: string
  description?: string | null
  preferred_tags?: Record<string, string> | null
  default_transform_id: number | null
  optional: boolean
  base_only: boolean
  hazard_id?: number | null
  transform_spec?: SlotTransformSpec | null
}

interface ImpactModelKey {
  hazard: string
  name: string
  uid: string
}

interface ImpactModel {
  id: number
  hazard_id: number
  key: ImpactModelKey
  label: string
  description?: string
  slots: Slot[]
}

interface Profile {
  id: number
  property_type_id: UUID
  description?: string | null
  created_at?: string
  transform: Transform
}

interface ProfilesPayload {
  model: ImpactModel
  slot_profiles_by_type_id: Record<string, Record<UUID, Profile>>
}

const apiErrorMessage = (error: unknown, fallback: string) => (error as any)?.response?.data?.detail ?? fallback

function useQueryParam(key: string) {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search).get(key), [search, key])
}

const ClimateAnalyticsConfigure = () => {
  const history = useHistory()
  const { possible } = usePermissions()
  const { data: models, isLoading, isError, error } = useImpactModels()
  const [search, setSearch] = useState('')
  const [selectedUID, setSelectedUID] = useState<string | null>(null)
  const modelUID = useQueryParam('model')

  const filteredModels = useMemo(() => {
    if (!models) return []
    const query = search.trim().toLowerCase()
    if (!query) return models
    return models.filter((model: ImpactModel) =>
      [model.label, model.description].some(value => value?.toLowerCase().includes(query)),
    )
  }, [models, search])

  useEffect(() => {
    if (modelUID) setSelectedUID(modelUID)
  }, [modelUID])

  useEffect(() => {
    if (!selectedUID && models && models.length) {
      setSelectedUID(models[0].key.uid)
      history.replace({ search: `?model=${encodeURIComponent(models[0].key.uid)}` })
    }
  }, [selectedUID, models, history])

  const selectedModel: ImpactModel | undefined = useMemo(
    () => models?.find((model: ImpactModel) => model.key.uid === selectedUID),
    [models, selectedUID],
  )

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '280px minmax(0, 1fr)' },
        minHeight: '100%',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Box
        component="aside"
        sx={{
          borderRight: { md: '1px solid' },
          borderBottom: { xs: '1px solid', md: 0 },
          borderColor: 'divider',
          p: 2,
          minWidth: 0,
        }}
      >
        <Toolbar disableGutters sx={{ mb: 1, gap: 1, minWidth: 0 }}>
          <IconButton size="small" onClick={() => history.push(siteMap.ClimateAnalytics.path)} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, minWidth: 0 }} noWrap>
            Configure Vulnerability
          </Typography>
        </Toolbar>

        <TextField
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search models"
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ mt: 1.5, maxHeight: { xs: 220, md: 'none' }, overflowY: { xs: 'auto', md: 'visible' } }}>
          {isLoading && (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {isError && <Alert severity="error">Failed to load models: {String((error as any)?.message ?? error)}</Alert>}

          {!isLoading && !isError && (
            <List dense disablePadding>
              {filteredModels.map((model: ImpactModel) => {
                const active = selectedUID === model.key.uid
                return (
                  <ListItemButton
                    key={model.key.uid}
                    selected={active}
                    onClick={() => {
                      setSelectedUID(model.key.uid)
                      history.push({ search: `?model=${encodeURIComponent(model.key.uid)}` })
                    }}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={model.label}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 700 : 500 }}
                    />
                  </ListItemButton>
                )
              })}
            </List>
          )}
        </Box>
      </Box>

      <Box component="main" sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
        <Box sx={{ maxWidth: 1040, mx: 'auto', minWidth: 0 }}>
          {!possible(ClimateAnalyticsPermissions.Modify) ? (
            <Alert severity="warning">You do not have permission to modify climate vulnerability configuration.</Alert>
          ) : selectedModel ? (
            <ModelDetails model={selectedModel} />
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
              <Typography color="text.secondary">Select a model to configure</Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  )
}

function ModelDetails({ model }: { model: ImpactModel }) {
  const { data: modelPropertyTypeProfiles, isLoading, isError, error } = useImpactModelProfiles(model.key.uid)
  const { data: propertyTypes, isLoading: ptLoading, isError: ptError } = usePropertyTypes()

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        {model.label}
      </Typography>
      {model.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {model.description}
        </Typography>
      )}

      {isLoading || ptLoading ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading configuration…
          </Typography>
        </Stack>
      ) : null}

      {isError && <Alert severity="error">Failed to load profiles: {String((error as any)?.message ?? error)}</Alert>}
      {ptError && <Alert severity="error">Failed to load property types.</Alert>}

      {modelPropertyTypeProfiles && propertyTypes?.length ? (
        <Stack spacing={1.5}>
          {model.slots.map(slot => (
            <SlotConfiguration
              key={slot.id}
              slot={slot}
              payload={modelPropertyTypeProfiles as ProfilesPayload}
              propertyTypes={propertyTypes}
            />
          ))}
        </Stack>
      ) : null}

      {!isLoading && modelPropertyTypeProfiles && model.slots.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This model has no configurable slots.
        </Alert>
      )}
    </Box>
  )
}

function SlotConfiguration({
  slot,
  payload,
  propertyTypes,
}: {
  slot: Slot
  payload: ProfilesPayload
  propertyTypes: Array<{ id: UUID; label: string }>
}) {
  const { data: baseTransforms, isLoading: transformsLoading } = useBaseTransforms(slot.id, {})
  const [createdTransforms, setCreatedTransforms] = useState<Transform[]>([])
  const [editorOpen, setEditorOpen] = useState(false)
  const [browserOpen, setBrowserOpen] = useState(false)
  const [snack, setSnack] = useState<string | null>(null)

  const transforms = useMemo(() => {
    const byId = new Map<number, Transform>()
    ;[...baseTransforms, ...createdTransforms].forEach(transform => byId.set(transform.id, transform))
    return Array.from(byId.values())
  }, [baseTransforms, createdTransforms])

  const profilesForSlot = payload.slot_profiles_by_type_id?.[slot.ref] ?? {}
  const profiles = Object.values(profilesForSlot)
  const assignedTypeIds = new Set(Object.keys(profilesForSlot))
  const addableTypes = propertyTypes.filter(propertyType => !assignedTypeIds.has(propertyType.id))
  const propertyTypeLabelMap = useMemo(
    () => Object.fromEntries(propertyTypes.map(propertyType => [propertyType.id, propertyType.label])),
    [propertyTypes],
  )
  const defaultTransform = useMemo(() => {
    if (typeof slot.default_transform_id !== 'number') return null
    return transforms.find(transform => transform.id === slot.default_transform_id) ?? null
  }, [transforms, slot.default_transform_id])
  const requiresExplicitTransform = slot.optional && typeof slot.default_transform_id !== 'number'
  const fallbackLabel =
    defaultTransform?.label ??
    (typeof slot.default_transform_id === 'number'
      ? `Impact function ${slot.default_transform_id}`
      : requiresExplicitTransform
      ? 'Inactive until a property type is configured'
      : 'None')

  if (transformsLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading slot…
        </Typography>
      </Stack>
    )
  }

  return (
    <Accordion disableGutters sx={{ minWidth: 0, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minWidth: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.75, sm: 1 }}
          alignItems={{ sm: 'center' }}
          sx={{ minWidth: 0, flex: 1, pr: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>
            {slot.label}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {slot.optional && <Chip size="small" variant="outlined" label="Optional" />}
            {profiles.length > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${profiles.length} ${profiles.length === 1 ? 'configuration' : 'configurations'}`}
              />
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <Divider />
      <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={2}>
          {(slot.description || slot.transform_spec) && (
            <Box>
              {slot.description && (
                <Typography variant="body2" color="text.secondary">
                  {slot.description}
                </Typography>
              )}
              {slot.transform_spec && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {slot.transform_spec.input.label}
                  {slot.transform_spec.input.unit ? ` (${slot.transform_spec.input.unit})` : ''} →{' '}
                  {slot.transform_spec.output.label}
                  {slot.transform_spec.output.unit ? ` (${slot.transform_spec.output.unit})` : ''}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              sx={{ p: 2 }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Impact functions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {slot.transform_spec
                    ? 'Browse the available functions or create one for this slot.'
                    : 'This slot uses its existing platform configuration.'}
                </Typography>
              </Box>
              {slot.transform_spec && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityOutlinedIcon />}
                    onClick={() => setBrowserOpen(true)}
                  >
                    Browse
                  </Button>
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setEditorOpen(true)}>
                    Create
                  </Button>
                </Stack>
              )}
            </Stack>
            <Divider />
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}
            >
              <Typography variant="body2" color="text.secondary">
                General impact function:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {fallbackLabel}
              </Typography>
              <Tooltip title="Used for property types without a matching configuration.">
                <InfoOutlinedIcon
                  aria-label="About the general impact function"
                  sx={{ color: 'text.secondary', fontSize: 17 }}
                />
              </Tooltip>
            </Stack>
          </Box>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2, minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              sx={{ mb: profiles.length ? 1.5 : 0 }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Property type configurations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use a different impact function for a specific property type.
                </Typography>
              </Box>
              <AddProfileDialog
                slotId={slot.id}
                addableTypes={addableTypes}
                transforms={transforms}
                transformSpec={slot.transform_spec ?? null}
              />
            </Stack>

            {profiles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {requiresExplicitTransform
                  ? 'No configurations yet. This optional source is currently inactive.'
                  : 'No configurations yet.'}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {profiles.map(profile => (
                  <ProfileRow
                    key={profile.property_type_id}
                    profile={profile}
                    transforms={transforms}
                    propertyTypeLabelMap={propertyTypeLabelMap}
                    transformSpec={slot.transform_spec ?? null}
                    defaultTransform={defaultTransform}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </AccordionDetails>

      {slot.transform_spec && (
        <>
          <TransformEditorDialog
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            slot={{ id: slot.id, label: slot.label, transform_spec: slot.transform_spec }}
            templates={transforms}
            onCreated={transform => {
              setCreatedTransforms(current => [...current, transform])
              setSnack(`“${transform.label}” created. You can now assign it to a property type.`)
            }}
          />
          <ImpactFunctionBrowserDialog
            open={browserOpen}
            onClose={() => setBrowserOpen(false)}
            slotLabel={slot.label}
            transforms={transforms}
            spec={slot.transform_spec}
          />
        </>
      )}
      <Snackbar open={Boolean(snack)} autoHideDuration={3500} onClose={() => setSnack(null)} message={snack ?? ''} />
    </Accordion>
  )
}

function ProfileRow({
  profile,
  transforms,
  propertyTypeLabelMap,
  transformSpec,
  defaultTransform,
}: {
  profile: Profile
  transforms: Transform[]
  propertyTypeLabelMap: Record<UUID, string>
  transformSpec: SlotTransformSpec | null
  defaultTransform: Transform | null
}) {
  const [changeOpen, setChangeOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [snack, setSnack] = useState<string | null>(null)
  const [changeError, setChangeError] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [pendingTransformId, setPendingTransformId] = useState(profile.transform.id)
  const [previewTransform, setPreviewTransform] = useState<Transform | null>(null)

  const availableTransforms = useMemo(() => {
    const byId = new Map<number, Transform>([[profile.transform.id, profile.transform]])
    transforms.forEach(transform => byId.set(transform.id, transform))
    return Array.from(byId.values())
  }, [transforms, profile.transform])
  const transformLabelById = useMemo(
    () => Object.fromEntries(availableTransforms.map(transform => [transform.id, transform.label])),
    [availableTransforms],
  )
  const selectedTransform =
    availableTransforms.find(transform => transform.id === pendingTransformId) ?? profile.transform

  useEffect(() => setPendingTransformId(profile.transform.id), [profile.transform.id])

  const { mutate: updateTransform, isLoading: saving } = useUpdateSlotProfileTransform({
    onSuccess: () => {
      setChangeOpen(false)
      setChangeError(null)
      setSnack('Configuration updated; affected climate results are being recalculated')
    },
    onError: error => setChangeError(apiErrorMessage(error, 'Could not update configuration.')),
  })
  const { mutate: deleteProfile, isLoading: deleting } = useDeleteSlotProfile({
    onSuccess: () => {
      setRemoveOpen(false)
      setRemoveError(null)
      setSnack(
        defaultTransform
          ? 'Configuration removed; the general impact function now applies'
          : 'Optional configuration removed',
      )
    },
    onError: error => setRemoveError(apiErrorMessage(error, 'Could not remove configuration.')),
  })

  const openChangeDialog = () => {
    setPendingTransformId(profile.transform.id)
    setChangeError(null)
    setChangeOpen(true)
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(150px, 1fr) minmax(180px, 1fr) auto' },
        gap: 1.5,
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {propertyTypeLabelMap[profile.property_type_id] ?? `${profile.property_type_id.slice(0, 8)}…`}
        </Typography>
        {profile.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {profile.description}
          </Typography>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Impact function
        </Typography>
        <Typography variant="body2" noWrap title={profile.transform.label}>
          {profile.transform.label}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.5} justifyContent={{ lg: 'flex-end' }} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          startIcon={<VisibilityOutlinedIcon />}
          disabled={!transformSpec}
          onClick={() => setPreviewTransform(profile.transform)}
        >
          Preview
        </Button>
        <Button size="small" startIcon={<EditOutlinedIcon />} onClick={openChangeDialog}>
          Change
        </Button>
        <Tooltip title="Remove configuration">
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setRemoveError(null)
                setRemoveOpen(true)
              }}
              disabled={deleting}
              aria-label="Remove configuration"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Dialog open={changeOpen} onClose={() => !saving && setChangeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Change impact function</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id={`transform-${profile.id}`}>Impact function</InputLabel>
              <Select
                labelId={`transform-${profile.id}`}
                value={pendingTransformId}
                label="Impact function"
                onChange={event => setPendingTransformId(Number(event.target.value))}
                disabled={saving}
                renderValue={value => transformLabelById[Number(value)] ?? String(value)}
              >
                {impactFunctionOptions(availableTransforms)}
              </Select>
            </FormControl>
            {changeError && <Alert severity="error">{changeError}</Alert>}
            {transformSpec && selectedTransform && (
              <>
                <Divider />
                <ImpactFunctionVisualisation transform={selectedTransform} spec={transformSpec} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || pendingTransformId === profile.transform.id}
            onClick={() => updateTransform({ profileId: profile.id, transformId: pendingTransformId })}
          >
            Apply change
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removeOpen} onClose={() => !deleting && setRemoveOpen(false)}>
        <DialogTitle>Remove property type configuration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {defaultTransform
              ? 'The general impact function will apply to this property type.'
              : 'This optional source-specific impact function will no longer apply to this property type.'}
          </DialogContentText>
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {removeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" disabled={deleting} onClick={() => deleteProfile({ profileId: profile.id })}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={2500} onClose={() => setSnack(null)} message={snack ?? ''} />
      {transformSpec && (
        <TransformPreviewDialog
          open={Boolean(previewTransform)}
          onClose={() => setPreviewTransform(null)}
          transform={previewTransform}
          spec={transformSpec}
        />
      )}
    </Box>
  )
}

function AddProfileDialog({
  slotId,
  addableTypes,
  transforms,
  transformSpec,
}: {
  slotId: number
  addableTypes: Array<{ id: UUID; label: string }>
  transforms: Transform[]
  transformSpec: SlotTransformSpec | null
}) {
  const [open, setOpen] = useState(false)
  const [typeId, setTypeId] = useState<UUID>('')
  const [transformId, setTransformId] = useState<number | null>(null)
  const [description, setDescription] = useState('')
  const [snack, setSnack] = useState<string | null>(null)
  const [requestError, setRequestError] = useState<string | null>(null)
  const canSubmit = typeId !== '' && transformId !== null
  const transformLabelById = useMemo(
    () => Object.fromEntries(transforms.map(transform => [transform.id, transform.label])),
    [transforms],
  )
  const selectedTransform = transforms.find(transform => transform.id === transformId) ?? null

  const resetAndClose = () => {
    setOpen(false)
    setTypeId('')
    setTransformId(null)
    setDescription('')
    setRequestError(null)
  }

  const { mutate: createProfile, isLoading } = useCreateSlotProfile({
    onSuccess: () => {
      resetAndClose()
      setSnack('Property type configuration added')
    },
    onError: error => setRequestError(apiErrorMessage(error, 'Could not add configuration.')),
  })

  return (
    <>
      <Tooltip
        title={
          addableTypes.length === 0
            ? 'Every property type is already configured'
            : transforms.length === 0
            ? 'Create an impact function before adding a configuration'
            : ''
        }
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={addableTypes.length === 0 || transforms.length === 0}
            onClick={() => setOpen(true)}
          >
            Add configuration
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => !isLoading && resetAndClose()} maxWidth="md" fullWidth>
        <DialogTitle>Add property type configuration</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Choose the property type and the impact function it should use for this slot.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              <FormControl size="small" fullWidth>
                <InputLabel id={`property-type-${slotId}`}>Property type</InputLabel>
                <Select
                  labelId={`property-type-${slotId}`}
                  value={typeId}
                  label="Property type"
                  onChange={event => setTypeId(event.target.value as UUID)}
                >
                  {addableTypes.map(propertyType => (
                    <MenuItem key={propertyType.id} value={propertyType.id}>
                      {propertyType.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id={`impact-function-${slotId}`}>Impact function</InputLabel>
                <Select
                  labelId={`impact-function-${slotId}`}
                  value={transformId ?? ''}
                  label="Impact function"
                  renderValue={value => transformLabelById[Number(value)] ?? 'Select an impact function'}
                  onChange={event => {
                    const value = event.target.value as number | string
                    setTransformId(value === '' ? null : Number(value))
                  }}
                >
                  {impactFunctionOptions(transforms)}
                </Select>
              </FormControl>
            </Box>

            <TextField
              size="small"
              label="Description"
              helperText="Optional"
              value={description}
              onChange={event => setDescription(event.target.value)}
              fullWidth
            />

            {requestError && <Alert severity="error">{requestError}</Alert>}
            {transformSpec && selectedTransform && (
              <>
                <Divider />
                <ImpactFunctionVisualisation transform={selectedTransform} spec={transformSpec} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAndClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmit || isLoading}
            onClick={() =>
              createProfile({
                slotId,
                propertyTypeId: typeId,
                transformId,
                description: description || null,
              })
            }
          >
            Add configuration
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={2500} onClose={() => setSnack(null)} message={snack ?? ''} />
    </>
  )
}

export default ClimateAnalyticsConfigure
